import { RefObject, useLayoutEffect, useRef } from "react";

const EASE = "cubic-bezier(0.2, 0.8, 0.2, 1)";
const MOVE_MS = 320;
const EXIT_MS = 180;
const ENTER_MS = 280;
const ENTER_DELAY_MS = 90;
const ENTER_STAGGER_MS = 30;

type Snapshot = { rect: DOMRect; clone: HTMLElement };

const reducedMotion = () =>
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const relativeRect = (el: HTMLElement, origin: DOMRect) => {
  const box = el.getBoundingClientRect();
  return new DOMRect(
    box.left - origin.left,
    box.top - origin.top,
    box.width,
    box.height,
  );
};

const cloneRow = (row: HTMLElement) => {
  const clone = row.cloneNode(true) as HTMLElement;
  clone.classList.remove("animate-row-flash");
  clone.removeAttribute("data-row-key");
  return clone;
};

const stopAnimations = (el: HTMLElement) =>
  el.getAnimations().forEach((animation) => animation.cancel());

/**
 * Animates the keyed rows of a list whenever they change: rows that stay glide
 * to their new spot, new rows fade up in with a brief flash, removed rows fade
 * out where they were and the wrapper eases to its new height.
 *
 * Rows are the `[data-row-key]` elements inside `wrapperRef`. `ghostLayerRef`
 * is an empty, absolutely positioned child of the wrapper that the leaving
 * rows are drawn into, so React never sees the extra nodes.
 *
 * `version` must change exactly when the rows do. The first render and a new
 * `resetKey` (a different list altogether) are never animated.
 */
export const useRowTransitions = ({
  wrapperRef,
  ghostLayerRef,
  version,
  resetKey,
}: {
  wrapperRef: RefObject<HTMLElement | null>;
  ghostLayerRef: RefObject<HTMLElement | null>;
  version: string;
  resetKey?: string | number;
}) => {
  const snapshots = useRef(new Map<string, Snapshot>());
  const height = useRef(0);
  const last = useRef<{ version: string; resetKey?: string | number }>(
    undefined,
  );
  // Resizes during our own animations are ours; don't snapshot mid-flight.
  const busyUntil = useRef(0);

  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    const ghosts = ghostLayerRef.current;
    if (!wrapper || !ghosts) return;

    const previous = last.current;
    last.current = { version, resetKey };
    if (previous?.version === version && previous.resetKey === resetKey) {
      // Same rows, but text may have changed (labels load late): refresh the
      // clones so a later exit shows what was really on screen.
      for (const row of wrapper.querySelectorAll<HTMLElement>(
        "[data-row-key]",
      )) {
        const snapshot = snapshots.current.get(row.dataset.rowKey!);
        if (snapshot) snapshot.clone = cloneRow(row);
      }
      return;
    }
    const animate =
      previous !== undefined &&
      previous.resetKey === resetKey &&
      !reducedMotion();

    // Settle anything still running from a quick previous click first, so
    // every measurement below is the real layout.
    stopAnimations(wrapper);
    const rows = [...wrapper.querySelectorAll<HTMLElement>("[data-row-key]")];
    rows.forEach(stopAnimations);

    const origin = wrapper.getBoundingClientRect();
    const next = new Map<string, Snapshot>();
    for (const row of rows) {
      next.set(row.dataset.rowKey!, {
        rect: relativeRect(row, origin),
        clone: cloneRow(row),
      });
    }

    if (animate) {
      busyUntil.current = performance.now() + MOVE_MS + 50;
      let entering = 0;

      for (const row of rows) {
        const key = row.dataset.rowKey!;
        const now = next.get(key)!.rect;
        const before = snapshots.current.get(key)?.rect;
        if (before) {
          const dx = before.left - now.left;
          const dy = before.top - now.top;
          if (dx || dy) {
            row.animate(
              [
                { transform: `translate(${dx}px, ${dy}px)` },
                { transform: "translate(0, 0)" },
              ],
              { duration: MOVE_MS, easing: EASE },
            );
          }
          continue;
        }
        const delay = ENTER_DELAY_MS + entering * ENTER_STAGGER_MS;
        row.animate(
          [
            { opacity: 0, transform: "translateY(6px)", filter: "blur(3px)" },
            { opacity: 1, transform: "translateY(0)", filter: "blur(0)" },
          ],
          { duration: ENTER_MS, delay, easing: EASE, fill: "backwards" },
        );
        // Restart the CSS flash even if the class is still on from last time.
        row.classList.remove("animate-row-flash");
        void row.offsetWidth;
        row.style.animationDelay = `${delay}ms`;
        row.classList.add("animate-row-flash");
        entering++;
      }

      ghosts.replaceChildren();
      for (const [key, { rect, clone }] of snapshots.current) {
        if (next.has(key)) continue;
        Object.assign(clone.style, {
          position: "absolute",
          left: `${rect.left}px`,
          top: `${rect.top}px`,
          width: `${rect.width}px`,
          height: `${rect.height}px`,
          margin: "0",
        });
        ghosts.appendChild(clone);
        clone
          .animate(
            [
              { opacity: 1, transform: "translateY(0)", filter: "blur(0)" },
              {
                opacity: 0,
                transform: "translateY(-4px)",
                filter: "blur(3px)",
              },
            ],
            { duration: EXIT_MS, easing: "ease-in", fill: "forwards" },
          )
          .finished.then(
            () => clone.remove(),
            () => clone.remove(),
          );
      }

      if (height.current && height.current !== origin.height) {
        wrapper.animate(
          [
            { height: `${height.current}px`, overflow: "clip" },
            { height: `${origin.height}px`, overflow: "clip" },
          ],
          { duration: MOVE_MS, easing: EASE },
        );
      }
    } else {
      ghosts.replaceChildren();
    }

    snapshots.current = next;
    height.current = origin.height;
  });

  // Rows re-flow on resize without a version change; keep the snapshot true.
  useLayoutEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      if (performance.now() < busyUntil.current) return;
      const origin = wrapper.getBoundingClientRect();
      for (const row of wrapper.querySelectorAll<HTMLElement>(
        "[data-row-key]",
      )) {
        const snapshot = snapshots.current.get(row.dataset.rowKey!);
        if (snapshot) snapshot.rect = relativeRect(row, origin);
      }
      height.current = origin.height;
    });
    observer.observe(wrapper);
    return () => observer.disconnect();
  }, [wrapperRef]);
};
